/**
 * apiBase — single source of truth for the backend origin used by frontend
 * fetches.
 *
 * Defaults to same-origin in the browser, but can be overridden at build time
 * for split frontend/API deploys such as Railway-backed `api.tacostreet.co`.
 *
 * History: this used to read process.env.REACT_APP_BACKEND_URL from the
 * Emergent platform's deploy-time /app/frontend/.env. Stale builds shipped
 * with that var pointing at a preview host (e.g. preview-main-11.emergent.host),
 * so every fetch went to a dead URL after the production domain changed.
 * Same-origin remains the safe fallback when no explicit API base is provided.
 *
 * Render preview static sites copy the production rewrite that sends `/api/*`
 * to taco-backend-fu5r.onrender.com. A preview page must call the preview
 * backend directly and must not fall back to that production host.
 */
const configuredApiBase = (process.env.REACT_APP_API_BASE_URL || "").trim().replace(/\/+$/, "");

/** Production API hosts a preview page is never allowed to call. */
export const PRODUCTION_API_HOSTS = new Set([
  "taco-backend-fu5r.onrender.com",
  "ai.tacostreetlocating.com",
  "www.tacostreetlocating.com",
  "tacostreetlocating.com",
]);

/** Render service-preview hostnames look like `<slug>-pr-<number>.onrender.com`. */
export function isRenderPreviewHostname(hostname: string): boolean {
  return hostname.endsWith(".onrender.com") && /-pr-\d+(?:\.|$)/.test(hostname);
}

/**
 * Pair a preview frontend host with its backend preview.
 * Returns "" when the host is not a preview, or when the only candidate
 * would be the production API. Callers must fail closed on "".
 */
export function previewBackendOrigin(frontendHostname: string): string {
  if (!isRenderPreviewHostname(frontendHostname)) return "";
  let host = frontendHostname;
  if (host.includes("taco-frontend-1mbo")) {
    host = host.replace("taco-frontend-1mbo", "taco-backend-fu5r");
  } else if (host.includes("taco-frontend")) {
    host = host.replace("taco-frontend", "taco-backend");
  } else {
    return "";
  }
  if (
    host === frontendHostname
    || PRODUCTION_API_HOSTS.has(host)
    || !isRenderPreviewHostname(host)
  ) {
    return "";
  }
  return `https://${host}`;
}

export function isProductionApiUrl(url: string): boolean {
  try {
    return PRODUCTION_API_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
}

function resolveApiBase(): string {
  if (typeof window !== "undefined" && isRenderPreviewHostname(window.location.hostname)) {
    return previewBackendOrigin(window.location.hostname);
  }
  return configuredApiBase || (typeof window !== "undefined" ? window.location.origin : "");
}

export const API_BASE: string = resolveApiBase();
